import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { subscribeToBout, confirmRound, startNextRound, confirmBoutResult, updateBout, subscribeToDiscipline, subscribeToEvent, subscribeToEventBouts, releaseJudgeSeat, unlockJudgeSeat, startNextPoolBout, confirmPoolResults } from "../firebase/services";
import UpNextRow from "../components/UpNextRow";
import CodeQR from "../components/CodeQR";
import UnavailableView from "../components/UnavailableView";
import JudgeScoreBreakdown from "../components/JudgeScoreBreakdown";
import { deriveBonusAdjustedTotals, deriveConfirmedBoutResult } from "../utils/boutResult";
import { announceStatus } from "../utils/announceStatus";

// React Router reuses this route component when only :boutId changes. Key the
// actual bout screen by its route identity so per-bout UI state (especially the
// editable confirmation totals) cannot carry into the next pool bout.
export default function RefBoutRoute() {
  const { eventId, boutId } = useParams();
  return <RefBout key={`${eventId}:${boutId}`} eventId={eventId} boutId={boutId} />;
}

function RefBout({ eventId, boutId }) {
  const navigate = useNavigate();
  const [bout, setBout] = useState(null);
  const [boutLoaded, setBoutLoaded] = useState(false);
  const [disc, setDisc] = useState(null);
  const [event, setEvent] = useState(null);
  const [poolBouts, setPoolBouts] = useState([]);
  const [confirmScoreA, setConfirmScoreA] = useState("");
  const [confirmScoreB, setConfirmScoreB] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startingNextBout, setStartingNextBout] = useState(false);
  const [confirmingPool, setConfirmingPool] = useState(false);
  const [editingJudges, setEditingJudges] = useState(false);
  const [selectedJudges, setSelectedJudges] = useState([]);

  useEffect(() => {
    if (bout?.status === "confirmed") announceStatus("Bout confirmed");
    else if (bout?.confirmedRounds?.length) announceStatus(`Round ${bout.confirmedRounds.length} confirmed`);
  }, [bout?.status, bout?.confirmedRounds?.length]);

  useEffect(() => {
    const unsub = subscribeToBout(eventId, boutId, (data) => {
      setBout(data);
      setBoutLoaded(true);
    });
    return unsub;
  }, [eventId, boutId]);

  useEffect(() => {
    const unsub = subscribeToEvent(eventId, setEvent);
    return unsub;
  }, [eventId]);

  // Load discipline roster for judge assignment
  useEffect(() => {
    if (!bout?.disciplineId) return;
    const unsub = subscribeToDiscipline(eventId, bout.disciplineId, setDisc);
    return unsub;
  }, [eventId, bout?.disciplineId]);

  // Sibling pool bouts, for the NEXT UP / ON DECK preview at the bottom of the page —
  // the ref stays on this page for the whole bout and jumps straight to the next one
  // via "Start Next Bout" (handleStartNextBout), rarely visiting RefPool in between.
  useEffect(() => {
    if (!bout?.poolName || bout.phase !== "pool") { setPoolBouts([]); return; }
    const unsub = subscribeToEventBouts(eventId, (allBouts) => {
      setPoolBouts(allBouts.filter(b => b.disciplineId === bout.disciplineId && b.poolName === bout.poolName)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    });
    return unsub;
  }, [eventId, bout?.disciplineId, bout?.poolName, bout?.phase]);

  // Auto-populate final confirm — includes judge bonus points for ties.
  // Fires once every judge has submitted their full card for the bout's last round, whether or
  // not that round has been separately locked into confirmedRounds yet (normal flow merges the
  // round-confirm into the bout-confirm as one ref action, so it usually hasn't been).
  useEffect(() => {
    if (!bout) return;
    if (bout.status === "confirmed" || confirmScoreA) return;
    const judges = bout.judges || [];
    if (judges.length === 0) return;
    const submissions = bout.judgeSubmissions || {};
    const totalRounds = bout.totalRounds || 1;
    const currentRound = bout.currentRound || 0;
    const confirmedRounds = bout.confirmedRounds || [];
    const allRoundsConfirmed = confirmedRounds.length >= totalRounds;
    const isFinalRound = currentRound === totalRounds - 1;
    if (!isFinalRound && !allRoundsConfirmed) return;

    const allJudgesSubmittedFinal = judges.every(j => {
      const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
      return submissions[key]?.complete;
    });
    if (!allJudgesSubmittedFinal) return;

    // Start with confirmed round totals
    let gB = confirmedRounds.reduce((s, r) => s + (r.blue || 0), 0);
    let gR = confirmedRounds.reduce((s, r) => s + (r.red || 0), 0);

    // If the final round isn't locked in yet (normal flow), add its total from each judge's card.
    if (!allRoundsConfirmed) {
      judges.forEach(j => {
        const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
        const sub = submissions[key];
        const rounds = sub?.rounds || [];
        const round = Array.isArray(rounds) ? (rounds.find(r => r.index === currentRound) || rounds[currentRound]) : null;
        if (round) { gB += round.blue || 0; gR += round.red || 0; }
      });
    }

    // Add bonus points from judges who assigned them (for ties).
    const totals = deriveBonusAdjustedTotals({ scoreRed: gR, scoreBlue: gB, judges, submissions });
    setConfirmScoreA(String(totals.red));
    setConfirmScoreB(String(totals.blue));
  }, [bout]);

  if (!boutLoaded) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;
  if (!bout) return <UnavailableView eventId={eventId} />;

  const judges = bout.judges || [];
  const submissions = bout.judgeSubmissions || {};
  const isConfirmed = bout.status === "confirmed";
  const totalRounds = bout.totalRounds || 1;
  const currentRound = bout.currentRound || 0;
  const confirmedRounds = bout.confirmedRounds || [];
  const isFinalRound = currentRound === totalRounds - 1;
  const confirmedCurrentRound = confirmedRounds.find(r => r.roundIndex === currentRound);
  // A confirmed final round only happens for bouts confirmed under the old two-step flow
  // (round locked in separately from the bout) — treat that the same as a freshly-completed
  // final round rather than showing a nonexistent "start next round" step.
  const legacyFinalConfirmed = !!confirmedCurrentRound && isFinalRound;

  const judgesSubmittedCurrentRound = judges.filter(j => {
    const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
    const sub = submissions[key];
    const rounds = sub?.rounds || [];
    if (!Array.isArray(rounds)) return false;
    // Check by index field first, then fall back to array position
    return rounds.some((r, i) => (r.index != null ? r.index : i) === currentRound);
  });
  const allJudgesSubmittedRound = judgesSubmittedCurrentRound.length === judges.length && judges.length > 0;

  let roundBlue = 0, roundRed = 0;
  if (allJudgesSubmittedRound) {
    judges.forEach(j => {
      const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
      const sub = submissions[key];
      const rounds = sub?.rounds || [];
      const round = Array.isArray(rounds) ? (rounds.find(r => r.index === currentRound) || rounds[currentRound]) : null;
      if (round) { roundBlue += round.blue || 0; roundRed += round.red || 0; }
    });
  }

  const handleConfirmRound = async () => {
    setConfirming(true);
    try { await confirmRound(eventId, boutId, currentRound, { blue: roundBlue, red: roundRed }); }
    catch (err) { console.error(err); alert("Failed"); }
    setConfirming(false);
  };

  const handleStartNextRound = async () => {
    setStarting(true);
    try { await startNextRound(eventId, boutId, currentRound + 1); }
    catch (err) { console.error(err); alert("Failed to start next round"); }
    setStarting(false);
  };

  // Merges locking in the final round with confirming the bout result into one ref action —
  // the round's score never gets its own separate "Confirm Round" step to avoid, since it
  // only becomes visible to the ref once every judge has submitted their full card anyway.
  const handleConfirmFinal = async () => {
    if (!confirmScoreA || !confirmScoreB) return;
    let result;
    try {
      result = deriveConfirmedBoutResult({
        scoreRed: confirmScoreA,
        scoreBlue: confirmScoreB,
        fighterRed: bout.fighterRed,
        fighterBlue: bout.fighterBlue,
        phase: bout.phase,
      });
    } catch (err) {
      alert(err.message);
      return;
    }
    if (!window.confirm("Confirm this bout result?\n\nThis cannot be undone.")) return;
    setConfirming(true);
    try {
      if (!legacyFinalConfirmed) await confirmRound(eventId, boutId, currentRound, { blue: roundBlue, red: roundRed });
      await confirmBoutResult(eventId, boutId, { ...result, confirmedBy: "Ref" });
      const completedPool = bout.phase === "pool"
        && bout.poolName
        && poolBouts.length > 0
        && poolBouts.every((poolBout) => poolBout.id === boutId || poolBout.status === "confirmed");
      if (completedPool) {
        navigate(`/ref/pool/${eventId}/${bout.disciplineId}/${encodeURIComponent(bout.poolName)}`);
      }
    }
    catch (err) { console.error(err); alert("Failed"); }
    setConfirming(false);
  };

  const handleAddTiebreakerRound = async () => {
    if (!window.confirm("Scores are tied. Add a tiebreaker round?")) return;
    try {
      if (!legacyFinalConfirmed) await confirmRound(eventId, boutId, currentRound, { blue: roundBlue, red: roundRed });
      const updates = { totalRounds: totalRounds + 1, status: "scoring" };
      // Store original total if this is the first tiebreaker
      if (!bout.originalTotalRounds) {
        updates.originalTotalRounds = totalRounds;
      }
      judges.forEach(j => {
        const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
        if (submissions[key]?.complete) {
          updates[`judgeSubmissions.${key}.complete`] = false;
        }
      });
      await updateBout(eventId, boutId, updates);
      setConfirmScoreA("");
      setConfirmScoreB("");
    } catch (err) { console.error(err); alert("Failed to add tiebreaker round"); }
  };

  const handleSaveJudges = async () => {
    try {
      await updateBout(eventId, boutId, { judges: selectedJudges });
      setEditingJudges(false);
    } catch (err) { console.error(err); alert("Failed"); }
  };

  const handleKickJudge = async (judgeName) => {
    if (!window.confirm(`Remove ${judgeName}? This will reset the entire bout — all judges' scores and confirmed rounds will be cleared.`)) return;
    try {
      await releaseJudgeSeat(eventId, boutId, judgeName);
      announceStatus(`${judgeName} removed from the bout`);
    } catch (err) { console.error(err); alert("Failed to remove judge"); }
  };

  const handleStartNextBout = async () => {
    setStartingNextBout(true);
    try {
      const nextBoutId = await startNextPoolBout(eventId, bout.disciplineId, bout.poolName);
      if (nextBoutId) navigate(`/ref/${eventId}/${nextBoutId}`);
      else navigate(`/ref/pool/${eventId}/${bout.disciplineId}/${encodeURIComponent(bout.poolName)}`);
    }
    catch (err) { console.error(err); alert(`Failed to start next bout: ${err.message || err}`); }
    setStartingNextBout(false);
  };

  const handleConfirmPoolResults = async () => {
    if (!window.confirm(`Confirm the full results for ${bout.poolName}?\n\nThis cannot be undone.`)) return;
    setConfirmingPool(true);
    try {
      await confirmPoolResults(eventId, bout.disciplineId, bout.poolName);
      navigate(`/ref/pool/${eventId}/${bout.disciplineId}/${encodeURIComponent(bout.poolName)}`);
    } catch (err) { console.error(err); alert(`Failed to confirm pool results: ${err.message || err}`); }
    setConfirmingPool(false);
  };

  const handleUnlockJudge = async (judgeName) => {
    if (!window.confirm(`Unlock ${judgeName}'s seat? This disconnects any device currently signed in as them (their scores are kept) — useful if they're on two devices at once. They can rejoin from one.`)) return;
    try {
      await unlockJudgeSeat(eventId, boutId, judgeName);
    } catch (err) { console.error(err); alert("Failed to unlock"); }
  };

  const grandBlue = confirmedRounds.reduce((s, r) => s + (r.blue || 0), 0);
  const grandRed = confirmedRounds.reduce((s, r) => s + (r.red || 0), 0);
  const isElimination = bout.phase === "elimination";

  const boutIdx = poolBouts.findIndex(b => b.id === boutId);
  const nextBout = boutIdx >= 0 ? poolBouts[boutIdx + 1] : undefined;
  const onDeckBout = boutIdx >= 0 ? poolBouts[boutIdx + 2] : undefined;
  const allPoolBoutsConfirmed = poolBouts.length > 0 && poolBouts.every((poolBout) => poolBout.status === "confirmed");

  // Available judges = event-wide officials roster (not the fighters)
  const availableJudges = (event?.officials || []);
  const pool = bout.poolName ? disc?.pools?.find(p => p.name === bout.poolName) : null;

  return (
    <div className="min-h-screen p-5">
      <Link
        to={bout.poolName ? `/ref/pool/${eventId}/${bout.disciplineId}/${encodeURIComponent(bout.poolName)}` : "/"}
        className="text-gray-600 text-sm no-underline hover:text-gray-400">
        ← Home
      </Link>

      <div className="mt-4 mb-5 text-center">
        <div className="flex items-center justify-center gap-3 mb-2">
          <span className="font-display text-xl font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
          <span className="text-gray-600 text-sm">vs</span>
          <span className="font-display text-xl font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
        </div>
        <div className="text-gray-600 text-xs">
          {bout.disciplineName && <span>{bout.disciplineName} • </span>}
          {bout.poolName && <span>{bout.poolName} • </span>}
          {bout.phase === "pool" ? "5-pt" : "3-pt"} • {totalRounds}R
          {bout.time && <span> • {bout.time}</span>}
          {bout.location && <span> • {bout.location}</span>}
        </div>
      </div>

      {/* Shared official code (elimination) or shared pool code. */}
      {(bout.officialCode || bout.code || pool?.code) && (
        <div className="bg-dark-700 border border-dark-500 rounded-xl p-5 mb-5 text-center">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">{(bout.officialCode || bout.code) ? "BOUT CODE" : "POOL CODE"}</div>
          <div className="font-display text-4xl font-extrabold tracking-[0.3em] text-white">{bout.officialCode || bout.code || pool.code}</div>
          <CodeQR path={(bout.officialCode || bout.code)
            ? `/bout/join?code=${encodeURIComponent(bout.officialCode || bout.code)}`
            : `/pool/${eventId}/${bout.disciplineId}/${encodeURIComponent(bout.poolName)}?code=${pool.code}`} />
        </div>
      )}

      {/* Spectator Code (elimination bouts only — pool bouts use the pool's spectator code) */}
      {bout.spectateCode && (
        <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5 text-center">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-2">SPECTATOR CODE</div>
          <div className="font-display text-2xl font-extrabold tracking-[0.3em] text-gray-300">{bout.spectateCode}</div>
        </div>
      )}

      {/* Judges Assignment */}
      <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest">JUDGES ({judges.length})</div>
          {!editingJudges && (
            <button onClick={() => { setSelectedJudges([...judges]); setEditingJudges(true); }}
              className="text-xs text-gray-500 hover:text-gray-300 bg-transparent border-none cursor-pointer">Edit</button>
          )}
        </div>
        {!editingJudges ? (
          judges.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {judges.map(j => {
                const isActive = (bout.activeJudges || []).includes(j);
                return (
                  <div key={j} className="flex items-center gap-1 px-2.5 py-1 bg-dark-800 border border-dark-500 rounded-lg text-xs"
                    style={{ color: isActive ? "#4CAF50" : "#ccc" }}>
                    <span>{j}</span>
                    {isActive && (
                      <>
                        <button onClick={() => handleUnlockJudge(j)} aria-label={`Unlock ${j}'s seat and keep scores`}
                          className="min-w-6 min-h-6 text-gray-600 hover:text-yellow-500 bg-transparent border-none cursor-pointer text-xs ml-1" title="Unlock seat (keep scores)">🔓</button>
                        <button onClick={() => handleKickJudge(j)} aria-label={`Remove ${j} and clear scores`}
                          className="min-w-6 min-h-6 text-gray-600 hover:text-red-400 bg-transparent border-none cursor-pointer text-xs" title="Remove judge (clear scores)">✕</button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-600 text-xs">No judges assigned. Click Edit to assign.</p>
          )
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {availableJudges.sort().map(name => {
                const isSel = selectedJudges.includes(name);
                return (
                  <button key={name} aria-pressed={isSel} onClick={() => isSel ? setSelectedJudges(selectedJudges.filter(j => j !== name)) : setSelectedJudges([...selectedJudges, name])}
                    className="px-3 py-1.5 rounded-lg text-sm font-semibold border cursor-pointer transition-all"
                    style={{ background: isSel ? "#1B5E20" : "#111", borderColor: isSel ? "#81C784" : "#666", color: isSel ? "#fff" : "#ddd" }}>
                    {name} {isSel && "✓"}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditingJudges(false)} className="flex-1 p-2 bg-dark-800 border border-dark-500 rounded-lg text-gray-500 text-sm font-bold cursor-pointer">Cancel</button>
              <button onClick={handleSaveJudges} className="flex-1 p-2 bg-green-action-light text-white rounded-lg text-sm font-display font-bold border-none cursor-pointer">Save</button>
            </div>
          </>
        )}
      </div>

      {/* Confirmed Rounds */}
      {confirmedRounds.length > 0 && (
        <div className="bg-dark-700 border border-dark-500 rounded-xl overflow-hidden mb-5">
          <div className="grid grid-cols-[60px_1fr_auto_1fr] items-center p-3 border-b border-dark-500">
            <span />
            <span className="font-display text-xs font-bold text-center" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</span>
            <span className="px-2" />
            <span className="font-display text-xs font-bold text-center" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</span>
          </div>
          {confirmedRounds.map((r, i) => (
            <div key={i} className="grid grid-cols-[60px_1fr_auto_1fr] items-center p-3 border-b border-dark-500/50">
              <span className="font-display text-xs font-bold text-gray-500">Round {i + 1}</span>
              <span className="font-display text-xl font-extrabold text-center" style={{ color: "#E53935" }}>{r.red}</span>
              <span className="text-gray-700 font-bold px-2">–</span>
              <span className="font-display text-xl font-extrabold text-center" style={{ color: "#1E88E5" }}>{r.blue}</span>
            </div>
          ))}
          <div className="grid grid-cols-[60px_1fr_auto_1fr] items-center p-3 bg-dark-800">
            <span className="font-display text-xs font-bold text-gray-400">TOTAL</span>
            <span className="font-display text-2xl font-extrabold text-center" style={{ color: "#D32F2F" }}>{grandRed}</span>
            <span className="text-gray-600 font-bold px-2">–</span>
            <span className="font-display text-2xl font-extrabold text-center" style={{ color: "#1565C0" }}>{grandBlue}</span>
          </div>
        </div>
      )}

      {/* Current Round */}
      {!isConfirmed && judges.length > 0 && (() => {
        // Round is locked in — hold here until the ref explicitly opens the next one, so the
        // transition is a deliberate action. Skipped when the confirmed round is the bout's
        // last one (only reachable via the old two-step flow) — that falls through to the
        // final-round handling below instead, since there's no next round to start.
        if (confirmedCurrentRound && !legacyFinalConfirmed) {
          return (
            <div className="bg-dark-700 border border-green-action rounded-xl p-4 mb-5 text-center">
          <div className="font-display text-xs font-bold text-green-action-text tracking-widest mb-3">✓ ROUND {currentRound + 1} CONFIRMED</div>
              <div className="flex items-center justify-center gap-4 mb-4">
                <span className="font-display text-2xl font-extrabold" style={{ color: "#E53935" }}>{confirmedCurrentRound.red}</span>
                <span className="text-gray-600 font-bold">–</span>
                <span className="font-display text-2xl font-extrabold" style={{ color: "#1E88E5" }}>{confirmedCurrentRound.blue}</span>
              </div>
              <button onClick={handleStartNextRound} disabled={starting}
                className="w-full p-3 text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer"
            style={{ background: "linear-gradient(135deg, #2E7D32, #1B5E20)" }}>
                {starting ? "Starting..." : `▶ Start Round ${currentRound + 2}`}
              </button>
            </div>
          );
        }

        if (!isFinalRound) {
          return (
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5">
            <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">ROUND {currentRound + 1} OF {totalRounds}</div>
            {judges.map(judgeName => {
              const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
              const sub = submissions[key];
              const rounds = sub?.rounds || [];
              const thisRound = Array.isArray(rounds) ? rounds.find(r => r.index === currentRound) : null;
              const isActive = (bout.activeJudges || []).includes(judgeName);
              return (
                <div key={judgeName} className="flex items-center justify-between py-2 border-b border-dark-500/30 last:border-0">
                  <span className="text-sm font-semibold text-gray-300">{judgeName}</span>
                  <div className="flex items-center gap-2">
                    {thisRound ? (
                      <div className="flex items-center gap-2">
                        <span className="font-display font-extrabold" style={{ color: "#E53935" }}>{thisRound.red}</span>
                        <span className="text-gray-700 text-xs">–</span>
                        <span className="font-display font-extrabold" style={{ color: "#1E88E5" }}>{thisRound.blue}</span>
                <span className="text-green-action-text text-xs font-bold ml-1">✓</span>
                      </div>
                    ) : (
                      <span className={`text-xs font-bold ${isActive ? "text-blue-fighter-accent" : "text-gray-600"}`}>{isActive ? "Scoring..." : "Waiting"}</span>
                    )}
                  </div>
                </div>
              );
            })}
            {allJudgesSubmittedRound && (
              <div className="mt-4">
                <div className="bg-dark-800 rounded-lg p-3 mb-3 text-center">
                  <div className="text-xs text-gray-500 font-bold mb-1">ROUND {currentRound + 1} TOTAL</div>
                  <div className="flex items-center justify-center gap-4">
                    <span className="font-display text-2xl font-extrabold" style={{ color: "#E53935" }}>{roundRed}</span>
                    <span className="text-gray-600 font-bold">–</span>
                    <span className="font-display text-2xl font-extrabold" style={{ color: "#1E88E5" }}>{roundBlue}</span>
                  </div>
                </div>
                <button onClick={handleConfirmRound} disabled={confirming}
                  className="w-full p-3 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
                  {confirming ? "Confirming..." : `✓ Confirm Round ${currentRound + 1}`}
                </button>
              </div>
            )}
          </div>
          );
        }

        // ── FINAL ROUND — this round decides the bout. Its scores stay hidden from the ref
        // until every judge has submitted their full card (including any tiebreak bonus), so a
        // judge still working through the bonus step never looks like the round is already in.
        // Once everyone's in, round-confirm and bout-confirm collapse into one ref action.
        const allJudgesSubmittedFinal = judges.every(j => {
          const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
          return submissions[key]?.complete;
        });

        if (!allJudgesSubmittedFinal) {
          const submittedCount = judges.filter(j => {
            const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
            return submissions[key]?.complete;
          }).length;
          return (
            <div className="bg-dark-700 border border-dark-500 rounded-xl p-4 mb-5">
              <div className="font-display text-xs font-bold text-gray-500 tracking-widest mb-3">FINAL ROUND — {currentRound + 1} OF {totalRounds}</div>
              {judges.map(judgeName => {
                const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
                const complete = submissions[key]?.complete;
                const isActive = (bout.activeJudges || []).includes(judgeName);
                return (
                  <div key={judgeName} className="flex items-center justify-between py-2 border-b border-dark-500/30 last:border-0">
                    <span className="text-sm font-semibold text-gray-300">{judgeName}</span>
                    <div className="flex items-center gap-2">
                      {complete ? (
              <span className="text-green-action-text text-xs font-bold">✓ Submitted</span>
                      ) : (
                        <span className={`text-xs font-bold ${isActive ? "text-blue-fighter-accent" : "text-gray-600"}`}>{isActive ? "Scoring..." : "Waiting"}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="mt-4 text-center">
                <div className="font-display text-xs font-bold text-yellow-500 tracking-widest mb-2">WAITING FOR FINAL SCORES</div>
                <p className="text-gray-500 text-xs mb-2">This round decides the bout — scores will show once every judge submits their full card.</p>
                <div className="font-display text-lg font-bold text-gray-400">{submittedCount}/{judges.length} submitted</div>
              </div>
            </div>
          );
        }

        // Judge bonus points are part of the final total. Use the same totals that populate
        // the confirmation fields so a bonus-adjusted tie cannot bypass the tiebreaker prompt.
        const finalTotals = deriveBonusAdjustedTotals({
          scoreRed: legacyFinalConfirmed ? grandRed : grandRed + roundRed,
          scoreBlue: legacyFinalConfirmed ? grandBlue : grandBlue + roundBlue,
          judges,
          submissions,
        });
        const finalRed = finalTotals.red;
        const finalBlue = finalTotals.blue;
        const isTiedElimFinal = isElimination && finalBlue === finalRed;

        return (
        <div className="bg-dark-700 border border-yellow-700 rounded-xl p-4 mb-5">
          <JudgeScoreBreakdown
            judges={judges}
            submissions={submissions}
            fighterRed={bout.fighterRed}
            fighterBlue={bout.fighterBlue}
          />
          {/* Tied elimination — must add tiebreaker round */}
          {isTiedElimFinal ? (
            <>
              <div className="font-display text-xs font-bold text-red-500 tracking-widest mb-3">⚠ TIED — TIEBREAKER REQUIRED</div>
              <div className="bg-dark-800 rounded-lg p-3 mb-3 text-center">
                <div className="text-xs text-gray-500 mb-1">Current Score</div>
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="text-[10px] font-bold" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</div>
                    <span className="font-display text-2xl font-extrabold" style={{ color: "#E53935" }}>{finalRed}</span>
                  </div>
                  <span className="text-gray-600 font-bold">–</span>
                  <div className="text-center">
                    <div className="text-[10px] font-bold" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</div>
                    <span className="font-display text-2xl font-extrabold" style={{ color: "#1E88E5" }}>{finalBlue}</span>
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-xs text-center mb-3">Elimination bouts cannot end in a tie. Add a tiebreaker round for the judges to score.</p>
              <button onClick={handleAddTiebreakerRound}
                className="w-full p-3 text-white font-display font-bold rounded-lg text-sm tracking-wide border-none cursor-pointer"
                style={{ background: "linear-gradient(135deg, #FF9800, #F57C00)" }}>
                + Add Tiebreaker Round {totalRounds + 1}
              </button>
            </>
          ) : (
            <>
              <div className="font-display text-xs font-bold text-yellow-500 tracking-widest mb-3">CONFIRM BOUT RESULT</div>

              {/* Show bonus points from judges */}
              {(() => {
                const bonuses = (bout.judges || []).map(j => {
                  const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
                  const sub = (bout.judgeSubmissions || {})[key];
                  if (sub?.complete && (sub.boutWinner === "blue" || sub.boutWinner === "red")) {
                    return { judge: j, to: sub.boutWinner === "blue" ? bout.fighterBlue : bout.fighterRed, color: sub.boutWinner === "blue" ? "#1E88E5" : "#E53935", comment: sub.boutWinnerComment };
                  }
                  return null;
                }).filter(Boolean);
                if (bonuses.length > 0) {
                  return (
                    <div className="bg-dark-800 rounded-lg p-3 mb-3">
                      <div className="text-xs font-bold text-yellow-600 mb-2">TIEBREAK BONUS (+1)</div>
                      {bonuses.map((b, i) => (
                        <div key={i} className="py-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-400">{b.judge}</span>
                            <span className="font-display font-bold" style={{ color: b.color }}>+1 → {b.to}</span>
                          </div>
                          {b.comment && <div className="text-[11px] text-gray-500 mt-0.5 italic">"{b.comment}"</div>}
                        </div>
                      ))}
                    </div>
                  );
                }
                return null;
              })()}

              {(() => {
                const drawJudges = (bout.judges || []).filter(j => {
                  const key = j.replace(/[\.\#\$\/\[\]]/g, "_");
                  const sub = (bout.judgeSubmissions || {})[key];
                  return sub?.complete && sub.confirmedDraw === true;
                });
                return drawJudges.length > 0 ? (
                  <div className="bg-dark-800 rounded-lg p-3 mb-3">
                    <div className="text-xs font-bold text-gray-400 mb-2">DRAW CONFIRMED — NO BONUS</div>
                    {drawJudges.map(j => <div key={j} className="text-xs text-gray-500 py-1">{j}</div>)}
                  </div>
                ) : null;
              })()}

              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1">
                  <label className="text-xs font-bold mb-1 block" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</label>
                  <input type="number" aria-label={`Final score for ${bout.fighterRed}`} value={confirmScoreA} onChange={(e) => setConfirmScoreA(e.target.value)}
                    className="w-full p-2 bg-dark-800 border rounded-lg text-center font-display text-2xl font-extrabold outline-none"
                    style={{ borderColor: "#D32F2F44", color: "#E53935" }} min="0" />
                </div>
                <span className="text-gray-600 font-bold mt-5">–</span>
                <div className="flex-1">
                  <label className="text-xs font-bold mb-1 block" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</label>
                  <input type="number" aria-label={`Final score for ${bout.fighterBlue}`} value={confirmScoreB} onChange={(e) => setConfirmScoreB(e.target.value)}
                    className="w-full p-2 bg-dark-800 border rounded-lg text-center font-display text-2xl font-extrabold outline-none"
                    style={{ borderColor: "#1565C044", color: "#1E88E5" }} min="0" />
                </div>
              </div>
              <div className="mb-3">
                <label className="text-xs font-bold text-gray-500 mb-1 block">Winner</label>
                <div className="p-2 rounded-lg text-center text-xs font-display font-bold border border-dark-500 bg-dark-800 text-gray-300">
                  {Number(confirmScoreA) > Number(confirmScoreB)
                    ? bout.fighterRed
                    : Number(confirmScoreB) > Number(confirmScoreA)
                    ? bout.fighterBlue
                    : confirmScoreA && confirmScoreB
                    ? (isElimination ? "Tiebreaker required" : "Draw")
                    : "Enter final scores"}
                </div>
              </div>
              <button onClick={handleConfirmFinal} disabled={confirming || !confirmScoreA || !confirmScoreB}
                className="w-full p-3 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
                {confirming ? "Confirming..." : "✓ Confirm Bout Result"}
              </button>
            </>
          )}
        </div>
        );
      })()}

      {/* No judges warning */}
      {!isConfirmed && judges.length === 0 && (
        <div className="bg-dark-700 border border-yellow-700 rounded-xl p-4 mb-5 text-center">
          <div className="text-yellow-500 text-sm font-bold mb-1">No judges assigned</div>
          <div className="text-gray-600 text-xs">Assign judges above before scoring can begin.</div>
        </div>
      )}

      {/* Confirmed */}
      {isConfirmed && bout.confirmedResult && (
        <div className="bg-dark-700 border border-green-action rounded-xl p-5 text-center">
            <div className="font-display text-xs font-bold text-green-action-text tracking-widest mb-3">✓ BOUT CONFIRMED</div>
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              <div className="text-xs font-bold mb-1" style={{ color: "#EF9A9A" }}>{bout.fighterRed}</div>
              <div className="font-display text-4xl font-extrabold" style={{ color: "#D32F2F" }}>{bout.confirmedResult.scoreA}</div>
            </div>
            <span className="text-gray-600 text-xl font-bold">–</span>
            <div className="text-center">
              <div className="text-xs font-bold mb-1" style={{ color: "#64B5F6" }}>{bout.fighterBlue}</div>
              <div className="font-display text-4xl font-extrabold" style={{ color: "#1565C0" }}>{bout.confirmedResult.scoreB}</div>
            </div>
          </div>
          {bout.confirmedResult.winner && bout.confirmedResult.winner !== "Draw" && (
            <div className="text-green-action-text font-display font-bold text-sm mt-3">Winner: {bout.confirmedResult.winner}</div>
          )}
        </div>
      )}

      {/* Start Next Bout (pool bouts only — advances the pool and jumps straight into the next bout) */}
      {isConfirmed && bout.phase === "pool" && bout.poolName && !allPoolBoutsConfirmed && (
        <button onClick={handleStartNextBout} disabled={startingNextBout}
          className="w-full p-4 mt-5 text-white font-display text-base font-bold rounded-xl tracking-wide disabled:opacity-40 border-none cursor-pointer"
            style={{ background: "linear-gradient(135deg, #2E7D32, #1B5E20)" }}>
          {startingNextBout ? "Starting..." : "▶ Start Next Bout"}
        </button>
      )}

      {isConfirmed && bout.phase === "pool" && bout.poolName && allPoolBoutsConfirmed && pool?.status !== "confirmed" && (
        <div className="bg-dark-700 border border-yellow-700 rounded-xl p-4 mt-5 text-center">
          <div className="font-display text-xs font-bold text-yellow-500 tracking-widest mb-2">ALL POOL BOUTS COMPLETE</div>
          <p className="text-gray-500 text-xs mb-3">Confirm the full pool results to publish the completed lineup.</p>
          <button onClick={handleConfirmPoolResults} disabled={confirmingPool}
            className="w-full p-3.5 bg-green-action-light text-white font-display font-bold rounded-lg text-sm tracking-wide disabled:opacity-40 border-none cursor-pointer">
            {confirmingPool ? "Confirming..." : <>&#10003; Confirm Pool Results</>}
          </button>
        </div>
      )}

      {(nextBout || onDeckBout) && (
        <div className="mt-5">
          <UpNextRow nextBout={nextBout} onDeckBout={onDeckBout} />
        </div>
      )}
    </div>
  );
}
